import { ConcurrencySweepChart, InferenceShiftChart, StackContract, VerticalIntegrationChart } from '@/components/charts';
import type { Block, DocSection } from '@/components/investor';

/**
 * Fnk investor overview. Mirrors docs/investors/fnk/investors.md; every figure
 * comes from docs/business-plan/fnk-business-plan.md — keep the three in sync.
 */

export const investorsStatus = 'TRL 6 · Benchmark gate passed · Zero paying customers · Built on ≈€23,000';

export const investorsSummary =
  '**The AI stack, the Harness and the inference underneath enterprise AI** — an agent framework, a control layer and a reactive inference engine built as one non-blocking system, licensed per GPU on a buyer’s own hardware or delivered as a complete box where nothing leaves the customer’s environment. A working framework, control layer and inference engine; a first customer running its production workload on them ([DialogMe](https://dialogme.ai)); zero paying customers. Fnk is an FNK Labs product. Every financial figure below is a management scenario, not contracted revenue.';

export const investorsTop: Block[] = [
  {
    kind: 'callout',
    title: 'First customer — DialogMe',
    text: 'DialogMe, a conversational AI platform, is Fnk’s first customer and runs its production workload on the Fnk stack. Its use of the stack carries no licence fee today.',
  },
];

export const investorsSections: DocSection[] = [
  {
    title: 'The thesis',
    blocks: [
      {
        kind: 'text',
        text: 'Enterprises want private AI over their own data and cannot assemble it. Inference serving, model lifecycle, orchestration, retrieval, identity, business systems, observability, governance and operations are **nine disciplines, and a mid-market enterprise has one or two.** So they send confidential content to a third-country API against their own policy, or the project never leaves the pilot.',
      },
      {
        kind: 'text',
        text: 'The operators who do run GPUs pay for accelerators that wait on software. The standard stack bolts a LangChain-class framework onto vLLM across an HTTP boundary that carries **neither capacity upward nor cancellation downward**: work keeps entering a saturated runtime, and abandoned requests keep generating tokens. **The waste is architectural.**',
      },
      {
        kind: 'cards',
        cols: 2,
        items: [
          { title: 'The technical bet is settled', text: 'Measured head to head against the assembled open-source alternative on an NVIDIA GPU and won at every tested concurrency, by **1.4× to 2.0×**, with **2.0×** the completed requests per second and **1.6× lower latency** at the top of the sweep.' },
          { title: 'The commercial bet has not started', text: 'No paying customers yet. The entire remaining risk is commercial, and the plan removes it in the cheapest order: references first, paid assessments as the entry point, the box only after revenue buys the help to deliver it.' },
          { title: 'The licence is priced per GPU', text: 'Per GPU per year, one list price per hardware class, beneath the NVIDIA AI Enterprise line the buyer already budgets.' },
          { title: 'The destination is a standard, not a niche', text: 'The framework and engine a data centre standardises on, an OEM ships preinstalled, and an enterprise budgets per GPU — the way SAP and Oracle became the assumed component one layer up. In no modelled number here.' },
        ],
      },
    ],
  },
  {
    title: 'Vision, mission and positioning',
    blocks: [
      {
        kind: 'matrix',
        head: ['', 'Statement'],
        rows: [
          ['Vision', 'LLMs are replacing buttons and forms as the interface to services; behind every conversation runs an agentic workload that automates the service and makes it intelligent — Fnk builds the tools and the product that workload runs on'],
          ['Mission', 'Turn open-weight models into a governed, private and efficient AI service on hardware the customer controls — and prove every claim on the customer’s own workload'],
          ['Category', 'Enterprise AI infrastructure — the layers under the application'],
          ['Positioning — one line', '*Enterprise AI, built as one system*'],
        ],
        accentRows: [0],
      },
      {
        kind: 'matrix',
        head: ['Buyer', 'The line', 'Form of sale'],
        rows: [
          ['Inference providers, data centres, enterprise GPU estates, OEMs', '*More completed work from every GPU you already run*', 'Stack licence, per GPU per year'],
          ['Regulated enterprises without a platform team', '*Private AI without a platform team*', 'Partial or full box'],
          ['Teams that want the stack without running GPUs', '*The same stack, operated for you*', 'SaaS subscription'],
        ],
        minWidth: '560px',
      },
      {
        kind: 'callout',
        text: 'For **inference operators and enterprises that run open-weight models on hardware they control**, **Fnk** is the AI stack — agent framework, control layer and inference engine built as one non-blocking system — and the Harness that turns it into a governed service. **Unlike** the assembled stack, where a framework talks to an inference server across an HTTP boundary that carries neither capacity up nor cancellation down, Fnk shares one capacity contract across every layer — measured at **2.0×** the completed requests per second on the same NVIDIA GPU (see What has been measured, with its caveats).',
      },
      {
        kind: 'matrix',
        head: ['Fnk is', 'Fnk is not'],
        rows: [
          ['The framework, control layer, engine and Harness, from one accountable vendor', 'A model lab — frontier-model quality is not our game'],
          ['Private by construction — no third party’s licence, connectivity or permission needed to install it', 'A cloud assistant that needs the customer’s data to leave its environment'],
          ['Priced per GPU, one list price per hardware class, the same for every buyer', 'Priced on a share of measured savings'],
          ['Hardware- and model-independent', 'A CUDA-kernel competitor'],
        ],
      },
      {
        kind: 'text',
        text: 'The strategy that follows from this is the go-to-market section below; the ambition beyond the plan closes the document. The full statement is on [fnk.ai/vision](https://fnk.ai/vision).',
      },
    ],
  },
  {
    title: 'What the product is',
    blocks: [
      {
        kind: 'matrix',
        head: ['Form', 'Price', 'What it is', 'Who it is for'],
        rows: [
          ['SaaS subscription', 'Monthly or annual', 'The Fnk stack operated by Fnk — framework, control layer and inference engine as a managed service', 'Teams that want the stack without running GPUs'],
          ['Stack licence', 'Per GPU per year', 'Framework, control layer and inference engine, on the buyer’s own GPUs', 'Inference providers, data centres, enterprise GPU estates, OEMs'],
          ['Partial box', '≈€200k year one · €145k recurring', 'Stack, Harness, connectors and console on the customer’s hardware', 'Enterprises with a data centre'],
          ['Full box', '≈€290k year one · €150k recurring', 'The same, on licensed hardware, air-gapped if required', 'Regulated organisations without a platform team'],
        ],
        minWidth: '640px',
      },
      {
        kind: 'text',
        text: '**Both boxes are the same build**, differing only in who owns the hardware. **The first money is a paid assessment** — €12,000–€35,000, small enough for a department budget, replaying the buyer’s own workload on the Fnk stack — which is the qualification instrument rather than the objective.',
      },
      {
        kind: 'matrix',
        head: ['What the Harness holds', ''],
        rows: [
          ['Vertical workflow packs', 'Intent-to-transaction flows per service type'],
          ['Tool and action schemas', 'Tool definitions and the action cards that carry them'],
          ['Prompt and policy packs', 'Escalation, refusal and handover thresholds; Spanish and co-official languages'],
          ['Guardrails and confirmation gates', 'Nothing that costs money executes without explicit confirmation'],
          ['Grounding configuration', 'Retrieval bound to each user’s real entitlements in the source system'],
          ['Evaluation sets', 'The customer’s own questions, rerun on every model or connector upgrade, with rollback'],
        ],
        minWidth: '480px',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'What Fnk is deliberately not',
        text: 'It does not train foundation models — the integration stops at the model weights, which are open-weight and third-party.',
      },
    ],
  },
  {
    title: 'Vertical integration — the structural argument',
    blocks: [
      { kind: 'node', node: <VerticalIntegrationChart /> },
      {
        kind: 'matrix',
        head: ['Consequence', 'Why it follows', 'Where it shows up'],
        rows: [
          ['Fewer accelerators for the same work', 'The engine signals capacity upward and the framework stops admitting work a saturated runtime cannot finish; abandoned work is cancelled at the GPU and the reclaimed compute is counted', 'The 2.0× completion rate — fewer GPUs to license and to buy'],
          ['Private and air-gapped deployment is possible at all', 'No third party’s licence, connectivity or permission is needed to install the whole system inside a customer’s network', 'The box, and the market segment cloud-only competitors structurally cannot serve'],
          ['One line of accountability', 'A defect is ours to fix at whichever layer it sits', 'The argument that wins against self-assembled open-source stacks'],
          ['Procurement can sign a reference architecture', 'One party is accountable for the outcome', 'What turns four vendors pointing at each other into a purchase order'],
        ],
        minWidth: '640px',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'The honest limits',
        text: 'Vertical integration only pays if each layer is genuinely good — owning a mediocre inference engine is worse than renting an excellent one, which is exactly what the benchmark was built to test. It does not extend to the model. And a customer already standardised on a model vendor’s platform is buying a smaller change from a competitor built on it.',
      },
    ],
  },
  {
    title: 'What has been measured',
    blocks: [
      { kind: 'node', node: <StackContract /> },
      {
        kind: 'matrix',
        head: ['At maximum tested concurrency (64)', 'Fnk AI stack vs assembled stack'],
        rows: [
          ['Completed requests per second', '**2.0× more**'],
          ['Weighted mean latency', '**1.6× lower**'],
          ['Stable-stage p95 latency', '**2.4× lower**'],
          ['Inference output-token throughput', '**1.4× higher**'],
        ],
        numeric: [1],
        minWidth: '360px',
      },
      { kind: 'node', node: <ConcurrencySweepChart /> },
      {
        kind: 'callout',
        title: 'The most important row for an investor is the control arm',
        text: 'A reactive Spring WebFlux backend still talking to vLLM over the same HTTP boundary matched the baseline at 4, ran slightly behind at 8, 16 and 32, and moved ahead — by 1.1× — only at 64. Making the application layer reactive is *not* where the gain is. **A competitor can adopt a reactive framework in a sprint and get almost none of this.**',
      },
      {
        kind: 'list',
        items: [
          '**A whole-stack result.** It does not prove one GPU engine beats another and must never be sold as an engine benchmark.',
          '**One measured run per stack per concurrency point.** Five points in the same direction, not a confidence interval. Repeated randomised runs are budgeted.',
          '**A first-stage order effect is visible and unexplained.** The next run randomises scenario order and discards a warm-up.',
          '**Engineering evidence, not an SLA.** Nothing is priced against it as a commitment.',
          '**Not yet independently reproduced** on someone else’s hardware. That is the first thing certification money buys.',
        ],
      },
    ],
  },
  {
    title: 'Hardware and model independence',
    blocks: [
      {
        kind: 'matrix',
        head: ['Platform', 'Evidence', 'Role'],
        rows: [
          ['NVIDIA CUDA', 'The paired benchmark, RTX 4090 / CUDA 13.0, TensorRT-LLM integration', 'The data-centre path and the default box'],
          ['Apple Silicon / MLX', '1.8× end-to-end throughput over the local blocking baseline; 2.8× faster mean time-to-first-token against LM Studio', 'Corroboration on a unified-memory accelerator, and the low-cost edge/dev path'],
          ['AMD CPU/GPU', 'Backend arm of the benchmark already runs on AMD Ryzen; engine port is on the roadmap', 'Second-source hardware, and price leverage in box configurations'],
        ],
      },
      {
        kind: 'text',
        text: 'The stack’s claim is at the scheduling, admission, cancellation and cross-layer-contract level — not at the CUDA kernel level, where we do not compete. Models are open-weight and swappable — Salamandra/ALIA, Gemma, the Mistral open family — and the selection rule is commercial: **the smallest model that passes the customer’s task-level quality, safety and language tests.**',
      },
    ],
  },
  {
    title: 'Pricing — the licence is priced per GPU',
    blocks: [
      {
        kind: 'text',
        text: '**Stack licence, per GPU per year** — one list price per hardware class, the same for every buyer and independent of any measured result. An eight-GPU H200-class cloud instance annualises to roughly USD 437,000; a buyer who avoids even one has made a recurring decision worth more than the licence.',
      },
      {
        kind: 'matrix',
        head: ['Box component', 'Full box, year one', 'Partial box, year one', 'Recurring', 'Margin'],
        rows: [
          ['Hardware (pass-through)', '€70,000', '—', '—', '≈10%'],
          ['Licence (stack + Harness)', '€95,000', '€95,000', '€95,000', '≈90%'],
          ['Integration', '€70,000', '€55,000', '—', '≈50%'],
          ['Connectors', '€35,000', '€30,000', '€30,000 / €35,000', '≈88%'],
          ['Support', '€20,000', '€20,000', '€20,000', '≈72%'],
          ['Total', '**≈€290,000**', '**≈€200,000**', '**€145,000–€150,000**', '**≈60% / 77% Y1 · ≈87% recurring**'],
        ],
        accentRows: [5],
        numeric: [1, 2, 3, 4],
        minWidth: '640px',
      },
      {
        kind: 'text',
        text: 'Blended: **≈60%** in the first year of a full box, **≈77%** for a partial box, **≈87%** in every recurring year. Year one carries hardware and services at low margin to win the deal; every year after is high-margin recurring software. **Installed base, not new bookings, is the number that matters after year two.**',
      },
    ],
  },
  {
    title: 'Strategy and go-to-market — references first',
    blocks: [
      {
        kind: 'text',
        text: '**This is the part of the plan that is a decision rather than a forecast, and it is the part we would most want an investor to interrogate.** Enterprise contracts are six figures a year, recurring, ≈87% margin after the first year — and, for a company of this size today, unsignable: cold contact to signature runs **3–12 months**, procurement asks for references a pre-revenue vendor cannot name, and a vendor-risk assessment fails on company age alone.',
      },
      {
        kind: 'matrix',
        head: ['Strategic choice', 'What it means'],
        rows: [
          ['Own every layer under the application', 'Framework, control layer, engine and Harness as one system. The gain appears only when capacity, admission and cancellation cross the inference boundary, so it cannot be copied one component at a time'],
          ['Sell in the unit the buyer already budgets', 'Per GPU per year, one list price per hardware class, independent of any measured result, beneath the NVIDIA AI Enterprise line'],
          ['References before enterprise', 'DialogMe’s production workload and paid efficiency assessments — neither needs connectors or identity integration, so both come before the box'],
          ['Let the buyer measure', 'The first purchase is the assessment; it ends in written acceptance thresholds, and its price does not depend on the result'],
          ['Europe first, built from Spain', 'AI Act evidence, data residency and air-gapped deployment as product features'],
        ],
        minWidth: '560px',
      },
      {
        kind: 'matrix',
        head: ['Phase', 'When', 'What it is'],
        rows: [
          ['1 · References', 'Development years', 'DialogMe, the first customer, in production on the stack; **paid efficiency assessments** replaying a buyer’s own workload on the Fnk stack'],
          ['2 · Enterprise, worked through events', 'Years 2–4', 'DES Málaga, Big Data & AI World, MWC/4YFN, the Huawei partner conference — twenty named targets per event, pre-booked meetings, paid-assessment proposals as the measure'],
          ['3 · Partner-led', 'Once delivery repeats', 'Integrators and OEMs, only after a second deployment in a vertical costs under 60% of the first'],
        ],
        accentRows: [0],
        minWidth: '560px',
      },
      {
        kind: 'callout',
        title: 'Why the first customer matters',
        text: 'DialogMe’s production workload hardens the framework and the engine against thousands of real conversations from real businesses, on owned infrastructure, before either is exposed to an enterprise acceptance test. It is a reference, not revenue.',
      },
    ],
  },
  {
    title: 'Why now',
    blocks: [
      { kind: 'node', node: <InferenceShiftChart /> },
      {
        kind: 'metrics',
        items: [
          { value: '≈56%', label: 'Enterprises now running or planning production inference on private infrastructure', tone: 'plain' },
          { value: 'In force', label: 'EU AI Act transparency obligations in force', tone: 'plain' },
          { value: '$4,500', label: 'NVIDIA AI Enterprise per GPU per year — the line the buyer already budgets', tone: 'plain' },
          { value: '≈5%', label: 'OEM gross margin on AI servers — short of software differentiation', tone: 'plain' },
        ],
      },
    ],
  },
  {
    title: 'Financial scenarios',
    blocks: [
      {
        kind: 'text',
        text: '**The scenarios are defined by how long development takes, not by how fast the product then sells.** Development is to a deliberately reduced Phase 1 — a sellable stack — sized at 94 identified person-days. Phase 2 — box packaging, identity, connectors, install and upgrade — is sized at 320 person-days precisely so it cannot start before revenue buys help.',
      },
      {
        kind: 'matrix',
        head: ['Scenario', 'Development', 'First revenue', 'Year-5 revenue', 'EBITDA-positive', 'Coverage'],
        rows: [
          ['Pessimistic', '24 months', 'Year 3 — €15,000', '€665,500', 'Year 4', '1.64×'],
          ['Base', '12 months', 'Year 2 — €20,000', '€1,786,500', 'Year 3', '1.24×'],
          ['Optimistic', '6 months', 'Year 1 — €10,000', '€4,264,500', 'Year 2', '1.83×'],
        ],
        accentRows: [1],
        numeric: [3, 5],
        minWidth: '640px',
      },
      {
        kind: 'text',
        text: 'Base case at Year 5: **nine installed customers** — six partial boxes and three full — and **€526,500 of recurring revenue**. Every line needs under one sixth of one percent of its market: partial box 6 of ≈4,300 (0.14%), full box 3 of ≈1,800 (0.17%); the stack licence — ≈2,400 addressable, and a provider-scale pool of ≈190 European operators worth ≈€70m–€236m a year at list price — is not in the base case at all. **The eighteen months between best and worst are worth €3,599,000 of Year-5 revenue.**',
      },
      {
        kind: 'text',
        text: 'Development costs **€22,076** in cash in year one and **€17,976** a year thereafter. **€0 of external funding is required in any scenario.** The plan is only moderately robust to price — box prices 30% lower still reach Year 5 profitable, but take cash below zero on the way — and fragile to time.',
      },
      {
        kind: 'metrics',
        items: [
          { value: '€1.2–2.0M', label: 'Seed modelled, base column — after independent reproduction and a first paying customer' },
          { value: '€5–8M', label: 'Pre-money valuation', tone: 'plain' },
          { value: '15–22%', label: 'Implied dilution', tone: 'plain' },
          { value: '18–30 mo', label: 'Runway at the resulting cost base', tone: 'plain' },
        ],
      },
      {
        kind: 'callout',
        title: 'Not yet, and what a seed buys',
        text: 'The recommendation of record is not to raise during development. Engineering 45% (four engineers a year early: inference, connectors, solutions, platform) · delivery capacity 17.5% · go-to-market 15% (an Iberia account executive, the event calendar) · benchmark and certification 7.5% (independent reproduction on rented data-centre GPUs, security audit, SAP certification) · working capital 10% · reserve 5%.',
      },
    ],
  },
  {
    title: 'Defensibility and IP',
    blocks: [
      {
        kind: 'cards',
        items: [
          { title: 'The contract across the inference boundary', text: 'Framework and engine as one system, built by one team. What lets one per-GPU licence cover the whole stack, and what a competitor cannot get by adopting a reactive framework.' },
          { title: 'Deployment choice, end to end', text: 'The same build runs in a private cloud, on-premises, or fully air-gapped — the segment a cloud-only competitor structurally cannot serve.' },
          { title: 'The connector layer', text: 'Identity, content repositories, ERP, CRM, service management, databases — maintained, versioned and supported. Where switching cost accumulates.' },
          { title: 'Permission-aware retrieval', text: 'Answers filtered by each user’s real entitlements in the source system — the most common way enterprise AI pilots fail their security review, solved.' },
          { title: 'The Harness', text: 'Provider-neutral configuration that ships inside every box and carries production traffic today.' },
          { title: 'Installed-base switching cost', text: 'Knowledge indexes, permission mappings, integration configuration, evaluation history — accumulated per customer. Bought, not built.' },
        ],
      },
      {
        kind: 'matrix',
        head: ['Patent candidate', 'Why it may be defensible', 'Prior-art risk'],
        rows: [
          ['Bidirectional capacity contract between framework and engine', 'Crosses a boundary existing systems treat as opaque', 'Reactive Streams, RSocket leases, TCP flow control are known art'],
          ['End-to-end cancellation with reclaimed-capacity accounting', 'Specific mechanism plus a measurable accounting output', 'Cooperative cancellation is known; the accounting may be novel'],
          ['Business-transaction budgeting across fan-out, tools and retries', 'Budget object spanning heterogeneous call types', 'Rate limiting and quotas are extensive prior art'],
          ['Admission scheduling by business priority and remaining budget', 'Couples scheduler to business semantics', 'Scheduler priority schemes are extensive prior art'],
        ],
        minWidth: '600px',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Whether any of this is patentable is a legal question',
        text: 'At most one or two narrow, mechanism-specific claims, preceded by a prior-art and freedom-to-operate search before any public disclosure; scheduling heuristics held as trade secret. A patent is a **sales asset before it is a litigation asset**. An acquirer can buy or build a framework and an engine; **what is hard to assemble afterwards is the contract between them** — and that argument only holds while the installed base and the connector suite are genuinely accumulating.',
      },
    ],
  },
  {
    title: 'The ambition, and the gap to it',
    blocks: [
      {
        kind: 'text',
        text: 'The modelled exit is **€8m–€16m** — a plan, with a schedule and a €150,000 downside to find out it was wrong. Beyond it sits one standard position: **become the standard inference layer, the way the enterprise standards became standard.** SAP, Oracle and Informatica are line items because they became the assumed component. The same position exists, unclaimed, one layer down: the framework and engine a data centre standardises on, an OEM ships preinstalled, and a GPU estate is budgeted against per GPU.',
      },
      {
        kind: 'matrix',
        head: ['What that position requires', 'Where we stand'],
        rows: [
          ['A measured, reproducible advantage over the default', '**Passed** — but one campaign, not yet independently reproduced'],
          ['A licence unit the buyer already budgets in', '**Priced** — per GPU, deliberately beneath the NVIDIA AI Enterprise line'],
          ['Reference deployments at real scale', '**None.** This is the whole gap'],
          ['OEM and data-centre distribution', '**Nothing signed**'],
          ['The switching cost of being assumed', 'Years away, and it is the thing that actually makes a standard'],
        ],
        minWidth: '480px',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        text: '**This claim is priced into no figure on this page, and it would take most of a decade.** Standards are won by distribution and defaults, not by benchmarks.',
      },
    ],
  },
  {
    title: 'Risks, stated plainly',
    blocks: [
      {
        kind: 'matrix',
        head: ['Risk', 'Why it is real', 'What reduces it'],
        rows: [
          ['Zero paying customers today', 'The whole commercial thesis is untested', 'Paid assessments at €12,000–€35,000 test it inside a department budget, before any box'],
          ['Time to market', 'Eighteen months of difference is worth €3,599,000 of Y5 revenue', 'A reduced Phase 1 of 94 person-days; Phase 2 sized so it cannot start before revenue buys help'],
          ['Benchmark not independently reproduced', 'One campaign, one lab, no confidence intervals', 'Randomised reruns budgeted; reproduction is the first use of any seed'],
          ['Open source improves for free', 'vLLM and the LangChain family are genuinely good and continuously improving', 'A written abandonment rule: if an external rerun lands below 1.2×, switch to integrating mature runtimes and cut scope to the box and connectors'],
          ['Revenue concentration', 'Nine installed customers and the year’s new deployments carry all of base-case Y5 revenue', 'Registered and monitored, not solved; the stack licence would diversify it and is not counted'],
          ['Most of Y5 revenue is non-recurring', 'New deployments are 66.1% of the base-case year; recurring share is 20–37% by Y5', 'Recurring share rises with every earlier start, and keeps rising after the plan'],
          ['Price', 'Box prices 30% lower still reach Y5 profitable but take cash below zero on the way', 'Every price benchmarked against a published comparable; the assessment qualifies willingness to pay before a box is quoted'],
          ['No primary customer research yet', 'Market sizing is desk research divided by modelled deal counts', 'The largest open item in the plan, and named as such'],
        ],
        minWidth: '640px',
      },
    ],
  },
];
